import { getCurrencyScaleAndFormatterForValues } from "helpers/formatters/currency-scale"
import { LOADING_CELL_TEXT } from "helpers/compare-frontier-table"
import { ERROR, OK } from "helpers/data-statuses"
import type { DataStatus } from "helpers/data-statuses"

const GROSS_MARKET_VALUE_TYPES: readonly string[] = ["portfolio", "experiment"]
const NOT_APPLICABLE_TEXT = "--"
const MISSING_VALUE_TEXT = "-"

export interface FrontierColumn {
  type?: string
  noData?: boolean
  isUnmodeled?: boolean
}

export interface FrontierDailyData {
  summary?: { gmv?: unknown }
}

export interface RowUnit {
  currencyScale: string
  currency?: string | null
}

export interface GrossMarketValueRowInput {
  columns: readonly FrontierColumn[]
  detailsData: readonly (FrontierDailyData | null | undefined)[]
  statuses: readonly (DataStatus | undefined)[]
  currency?: string | null
}

export interface GrossMarketValueRow {
  label: string
  fn: (item: FrontierDailyData | null | undefined, type?: string) => string
  unit?: RowUnit
}

function isGrossMarketValueType(type: string | undefined): boolean {
  return type !== undefined && GROSS_MARKET_VALUE_TYPES.includes(type)
}

function showsGrossMarketValue({ type, noData, isUnmodeled }: FrontierColumn): boolean {
  return isGrossMarketValueType(type) && !noData && !isUnmodeled
}

function readGrossMarketValue(dailyData: FrontierDailyData | null | undefined): unknown {
  return dailyData?.summary?.gmv
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value)
}

function isStillLoading(dailyData: FrontierDailyData | null | undefined, status: DataStatus | undefined): boolean {
  return !dailyData && status !== OK && status !== ERROR
}

export function buildGrossMarketValueRow({
  columns,
  detailsData,
  statuses,
  currency,
}: GrossMarketValueRowInput): GrossMarketValueRow {
  const label = "Gross Market Value"
  const eligibleIndexes = columns.flatMap((column, index) => showsGrossMarketValue(column) ? [index] : [])
  const isAwaitingColumns = eligibleIndexes.some(index => isStillLoading(detailsData[index], statuses[index]))
  const values = eligibleIndexes
    .map(index => readGrossMarketValue(detailsData[index]))
    .filter(isFiniteNumber)

  if (isAwaitingColumns) {
    return {
      label,
      fn: (_item, type) => isGrossMarketValueType(type) ? LOADING_CELL_TEXT : NOT_APPLICABLE_TEXT,
    }
  }

  if (values.length === 0) {
    return {
      label,
      fn: (_item, type) => isGrossMarketValueType(type) ? MISSING_VALUE_TEXT : NOT_APPLICABLE_TEXT,
    }
  }

  const { currencyScale, formatter: formatScaledValue } = getCurrencyScaleAndFormatterForValues(values)

  return {
    label,
    fn: (item, type) => {
      if (!isGrossMarketValueType(type)) return NOT_APPLICABLE_TEXT
      const value = readGrossMarketValue(item)
      return isFiniteNumber(value) ? formatScaledValue(value) : MISSING_VALUE_TEXT
    },
    unit: { currencyScale, currency },
  }
}
